import {
  LibraryFolderContentsSortDirection,
  LibraryFolderContentsSortField,
} from '@/graphql-types/globalTypes';

export type LibrarySortValue =
  | 'manual'
  | 'date-desc'
  | 'date-asc'
  | 'name-asc'
  | 'name-desc'
  | 'createdBy-asc'
  | 'createdBy-desc';

export const LIBRARY_SORT_OPTIONS: {
  value: LibrarySortValue;
  label: string;
}[] = [
  { value: 'manual', label: 'Manual order' },
  { value: 'date-desc', label: 'Date (Newest first)' },
  { value: 'date-asc', label: 'Date (Oldest first)' },
  { value: 'name-asc', label: 'Name (A–Z)' },
  { value: 'name-desc', label: 'Name (Z–A)' },
  { value: 'createdBy-asc', label: 'Created by (A–Z)' },
  { value: 'createdBy-desc', label: 'Created by (Z–A)' },
];

export const DEFAULT_LIBRARY_SORT: LibrarySortValue = 'manual';

export const toLibraryFolderContentsSortInput = (
  sort: LibrarySortValue
): {
  sortField: LibraryFolderContentsSortField;
  sortDirection: LibraryFolderContentsSortDirection;
} => {
  if (sort === 'manual') {
    return {
      sortField: LibraryFolderContentsSortField.MANUAL,
      sortDirection: LibraryFolderContentsSortDirection.ASC,
    };
  }

  const sortDirection = sort.endsWith('desc')
    ? LibraryFolderContentsSortDirection.DESC
    : LibraryFolderContentsSortDirection.ASC;

  if (sort.startsWith('name')) {
    return {
      sortField: LibraryFolderContentsSortField.NAME,
      sortDirection,
    };
  }

  if (sort.startsWith('createdBy')) {
    return {
      sortField: LibraryFolderContentsSortField.CREATED_BY,
      sortDirection,
    };
  }

  return {
    sortField: LibraryFolderContentsSortField.DATE,
    sortDirection,
  };
};

export type SortableLibraryResource = {
  _id: string;
  name: string;
  dateCreated?: string | null;
  createdByName?: string | null;
};

const compareText = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

const toTimestamp = (value?: string | null): number => {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

export const sortLibraryResourceItems = <T extends SortableLibraryResource>(
  items: T[],
  sort: LibrarySortValue
): T[] => {
  if (sort === 'manual') {
    return items;
  }

  const direction = sort.endsWith('desc') ? -1 : 1;

  return [...items].sort((left, right) => {
    let result = 0;

    if (sort.startsWith('date')) {
      result = toTimestamp(left.dateCreated) - toTimestamp(right.dateCreated);
    } else if (sort.startsWith('name')) {
      result = compareText(left.name, right.name);
    } else {
      result = compareText(left.createdByName || '', right.createdByName || '');
    }

    if (result !== 0) {
      return result * direction;
    }

    const nameCompare = compareText(left.name, right.name);
    if (nameCompare !== 0) {
      return nameCompare * direction;
    }

    return compareText(left._id, right._id) * direction;
  });
};
